import { computed, type ComputedRef } from 'vue';

interface StatusOption {
  value: string;
  label: string;
  expect?: Array<{ field: string; toBe: string }>;
  entity?: string[];
}

interface StatusGroup {
  label: string;
  options: StatusOption[];
}

type CrmEntityLike = {
  status: string;
  moderation_status: string;
  launch_status?: string | null;
};

export const useCrmStatus = () => {
  const statusMeta = computed<StatusGroup[]>(() => ([
    {
      label: 'status',
      options: [
        { value: 'draft', label: 'draft', entity: ['segment', 'activity'] },
        { value: 'active', label: 'active', entity: ['segment', 'activity'] },
        { value: 'archived', label: 'archived', entity: ['activity'] },
      ],
    },
    {
      label: 'moderation_status',
      options: [
        { value: 'on_review', label: 'on_review', expect: [{ field: 'status', toBe: 'active' }], entity: ['segment', 'activity'] },
        { value: 'declined', label: 'declined', expect: [{ field: 'status', toBe: 'active' }], entity: ['segment', 'activity'] },
        { value: 'for_rework', label: 'for_rework', expect: [{ field: 'status', toBe: 'active' }], entity: ['segment', 'activity'] },
        { value: 'approved', label: 'approved', expect: [{ field: 'status', toBe: 'active' }], entity: ['segment', 'activity'] },
      ],
    },
    {
      label: 'launch_status',
      options: [
        { value: 'paused', label: 'paused', expect: [{ field: 'status', toBe: 'active' }, { field: 'moderation_status', toBe: 'approved' }], entity: ['activity'] },
        { value: 'running', label: 'running', expect: [{ field: 'status', toBe: 'active' }, { field: 'moderation_status', toBe: 'approved' }], entity: ['activity'] },
        { value: 'completed', label: 'completed', expect: [{ field: 'status', toBe: 'active' }, { field: 'moderation_status', toBe: 'approved' }], entity: ['activity'] },
        { value: 'pending', label: 'pending', expect: [{ field: 'status', toBe: 'active' }, { field: 'moderation_status', toBe: 'approved' }], entity: ['activity'] },
      ],
    },
  ]));

  const getStatusGroupByStatus = (status: string) => {
    return statusMeta.value.find((statusGroup) => statusGroup.options.some((option) => option.value === status));
  };

  const getStatusGroupFromList = (sourceFilterStatus: string[]) => {
    return sourceFilterStatus.reduce((acc, status) => {
      const group = getStatusGroupByStatus(status);
      const key = group?.label;

      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = [status];
      } else {
        acc[key].push(status);
      }

      return acc;
    }, {} as Record<string, string[]>);
  };

  const isStatusDisabled = (
    baseFilters: Record<string, string[]>,
    status: string,
    statusOption?: StatusOption,
  ) => {
    const sourceStatus = statusOption
      || getStatusGroupByStatus(status)?.options.find((option) => option.value === status);

    if (!sourceStatus?.expect) return false;

    return !sourceStatus.expect.every((assert) => baseFilters[assert.field]?.includes(assert.toBe));
  };

  const sanitizeStatus = (sourceFilterStatus: string[]) => {
    const groupedFilters = getStatusGroupFromList(sourceFilterStatus);
    return sourceFilterStatus.filter((status) => !isStatusDisabled(groupedFilters, status));
  };

  const detectCrmStatus = (entity: CrmEntityLike) => {
    const { status, moderation_status: moderationStatus, launch_status: launchStatus } = entity;

    if (status === 'draft') return 'draft';
    if (status === 'active') {
      if (moderationStatus === 'on_review') return 'on_review';
      if (moderationStatus === 'declined') return 'declined';
      if (moderationStatus === 'for_rework') return 'for_rework';
      if (moderationStatus === 'approved') {
        if (launchStatus === 'paused') return 'paused';
        if (launchStatus === 'running') return 'running';
        if (launchStatus === 'completed') return 'completed';
        return 'approved';
      }
    }
    if (status === 'archived') return 'archived';

    return 'unknown';
  };

  const isCanBeArchived = (entity: CrmEntityLike) => {
    const status = detectCrmStatus(entity);
    return status === 'approved' || status === 'completed' || status === 'declined' || status === 'draft';
  };

  const isCanBeCloned = (entity: CrmEntityLike) => entity.moderation_status === 'approved';

  return {
    statusMeta,
    getStatusGroupFromList,
    sanitizeStatus,
    detectCrmStatus,
    isCanBeArchived,
    isCanBeCloned,
  };
};
